import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { lspAPI } from '../../services/api';
import { Shield, TrendingUp, Briefcase, CheckCircle2, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';

const LSPDashboard = () => {
  const [verification, setVerification] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [verificationRes, commitmentsRes] = await Promise.all([
        lspAPI.getVerification().catch(() => ({ data: null })),
        lspAPI.getCommitments().catch(() => ({ data: [] }))
      ]);
      setVerification(verificationRes.data);
      setCommitments(commitmentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVerificationStatus = () => {
    if (!verification) return { text: 'Not Started', color: 'bg-slate-100 text-slate-700', icon: Clock };
    if (verification.verification_status === 'approved') return { text: 'Verified', color: 'bg-green-100 text-green-700', icon: CheckCircle2 };
    if (verification.verification_status === 'pending') return { text: 'Pending Review', color: 'bg-yellow-100 text-yellow-700', icon: Clock };
    return { text: 'Action Required', color: 'bg-red-100 text-red-700', icon: Shield };
  };

  const verificationStatus = getVerificationStatus();
  const StatusIcon = verificationStatus.icon;

  return (
    <Layout title="LSP Dashboard">
      <div className="space-y-6 animate-slide-in-up" data-testid="lsp-dashboard">
        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all group glass-effect hover-lift">
            <CardHeader>
              <div className="w-14 h-14 gradient-lsp rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20 animate-pulse-glow">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-slate-900 dark:text-slate-100">Verification Status</CardTitle>
              <CardDescription>Complete verification to access marketplace</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <StatusIcon className="w-5 h-5" />
                <span className={`px-3 py-1.5 text-sm font-medium rounded-lg ${verificationStatus.color}`}>
                  {verificationStatus.text}
                </span>
              </div>
              <Link to="/lsp/verification">
                <Button className="w-full gradient-lsp border-0 hover:shadow-lg hover:shadow-purple-500/30 transition-all hover-scale">
                  {verification?.verification_status === 'approved' ? 'View Details' : 'Complete Verification'}
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all group glass-effect hover-lift">
            <CardHeader>
              <div className="w-14 h-14 gradient-secondary rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg shadow-cyan-500/20">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-slate-900 dark:text-slate-100">Deal Marketplace</CardTitle>
              <CardDescription>Browse and commit to available deals</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/lsp/marketplace">
                <Button 
                  variant="outline" 
                  className="w-full border-2 border-cyan-500 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 hover-scale"
                  disabled={verification?.verification_status !== 'approved'}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Browse Deals
                </Button>
              </Link>
              {verification?.verification_status !== 'approved' && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 text-center">Complete verification to access</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Commitments</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{commitments.length}</p>
                </div>
                <div className="w-12 h-12 gradient-lsp rounded-xl flex items-center justify-center shadow-lg">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in" style={{animationDelay: '0.1s'}}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Active</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{commitments.filter(c => c.commitment_status === 'admin_approved').length}</p>
                </div>
                <div className="w-12 h-12 gradient-success rounded-xl flex items-center justify-center shadow-lg">
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in" style={{animationDelay: '0.2s'}}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Pending</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{commitments.filter(c => c.commitment_status === 'requested').length}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-xl flex items-center justify-center shadow-lg">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift animate-scale-in" style={{animationDelay: '0.3s'}}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Liquidity</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    ${verification?.plaid_verified_liquidity_amount?.toLocaleString() || '0'}
                  </p>
                </div>
                <div className="w-12 h-12 gradient-accent rounded-xl flex items-center justify-center shadow-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Information Card */}
        <Card className="gradient-overlay border-purple-200 dark:border-purple-800 glass-effect animate-slide-in-up">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-slate-100">Getting Started as an LSP</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              {[
                { text: 'Complete liquidity verification with bank statement and Plaid', done: verification !== null },
                { text: 'Wait for admin approval of your verification', done: verification?.verification_status === 'approved' },
                { text: 'Browse anonymized deals in the marketplace', done: false },
                { text: 'Commit liquidity to deals that match your criteria', done: commitments.length > 0 },
                { text: 'Admin approves match and coordinates closing', done: false }
              ].map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className={`w-7 h-7 ${step.done ? 'gradient-success' : 'gradient-lsp'} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md`}>
                    {step.done ? (
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    ) : (
                      <span className="text-white text-xs font-bold">{index + 1}</span>
                    )}
                  </div>
                  <span className="text-slate-700 dark:text-slate-300">{step.text}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default LSPDashboard;